import {
    Activity,
    AlertTriangle,
    Bug,
    Database,
    Gauge,
    Radio,
    Rss,
    Server,
    ShieldX,
    Sparkles,
} from "lucide-react";
import { Fragment } from "react";
import { Link } from "@tanstack/react-router";

import { DepHealthTile } from "~/components/DepHealthTile";
import { GrafanaLink } from "~/components/GrafanaLink";
import { TableSkeleton } from "~/components/TableSkeleton";
import { useCurrentUser } from "~/lib/auth";
import { useCVEs } from "~/lib/cves";
import { useDepHealth } from "~/lib/health";
import { useHeuristicStats } from "~/lib/heuristic";
import { type FeedSourceRecord, useFeedSources, useIoCs, useIoCStats } from "~/lib/iocs";
import { useNocStream } from "~/lib/noc";

/** Seconds since the given ISO timestamp, or null if it can't be parsed. */
function secondsSince(iso?: string): number | null {
    if (!iso) return null;
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return null;
    return Math.max(0, Math.floor((Date.now() - ts) / 1000));
}

function formatRelative(iso?: string): string {
    const s = secondsSince(iso);
    if (s === null) return "never";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

/** How overdue is a feed? Uses scheduleSeconds × 2 as the tolerance floor. */
function lagSeconds(f: FeedSourceRecord): number {
    const since = secondsSince(f.lastRunAt);
    if (since === null) return Number.POSITIVE_INFINITY;
    return Math.max(0, since - f.scheduleSeconds);
}

export function DashboardHome(): React.ReactElement {
    // Phase 1G — SSE stream replaces TanStack polling for the headline metrics.
    // The stream emits one snapshot on connect, then a tick every 5s. On any
    // SSE error the hook falls back to a 10s poll of /noc/snapshot, and `isLive`
    // tracks which mode we're in so the header can decorate accordingly.
    const noc = useNocStream();
    // Stats compatibility shim — older render paths read `stats.data.total`,
    // `sightings24h`, `sightings1h`. Re-derive from the NOC payload so we don't
    // ship a second polling loop.
    const stats = {
        data: noc.data
            ? {
                  total: noc.data.iocsTotal,
                  sightings1h: noc.data.sightings1h,
                  sightings24h: noc.data.sightings24h,
              }
            : undefined,
    } as const;
    const feeds = useFeedSources();
    // Recent IoCs — table at the bottom of the page.
    const recent = useIoCs({ limit: 10, offset: 0 });
    // CVE catalog — KEV-sourced for now; total + ransomware subset headlines.
    const cves = useCVEs({ limit: 1, offset: 0 });
    const cvesRansomware = useCVEs({ ransomwareOnly: true, limit: 1, offset: 0 });
    // IoC type breakdown and per-feed contribution stats.
    const iocStats = useIoCStats();
    // Phase 2d — Heuristic Engine summary tiles. Polls /admin/heuristic/stats
    // every 10s. Endpoint is internal-role-gated; the hook handles 401 by
    // exposing isError without crashing the page.
    const heuristicStats = useHeuristicStats();

    // Phase 1G leftover #1 — dependency-health tiles. Admin-only on the BFF
    // (the endpoint 401s for non-admin), so we gate the whole section on the
    // current user's role to avoid lit-up error states for analysts/operators.
    const me = useCurrentUser();
    const isAdmin = (me.data?.roles ?? []).includes("admin");
    const depHealth = useDepHealth({ enabled: isAdmin });

    const feedsAll = feeds.data ?? [];
    const failingFeeds = feedsAll.filter((f) => f.lastStatus === "error");
    const healthyFeeds = feedsAll.filter((f) => f.lastStatus === "ok");
    // Worst-lagging feeds (excluding disabled); cap at 6 rows.
    const laggingFeeds = [...feedsAll]
        .filter((f) => f.enabled !== false)
        .sort((a, b) => lagSeconds(b) - lagSeconds(a))
        .slice(0, 6);

    // Prep IoC type cards — filter out empty types, sort by count desc.
    const iocTypeCards = (iocStats.data?.byType ?? [])
        .filter((b) => b.count > 0)
        .sort((a, b) => b.count - a.count);

    // Prep feed source cards — enabled feeds only, top 10 + foldout for rest.
    const enabledFeeds = feedsAll.filter((f) => f.enabled);
    const topFeeds = enabledFeeds
        .sort((a, b) => (b.ingestedCount ?? 0) - (a.ingestedCount ?? 0))
        .slice(0, 10);
    const restCount = Math.max(0, enabledFeeds.length - 10);

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon">
                                <Gauge size={14} aria-hidden />
                            </span>
                            Mission control
                        </span>
                        <h1 className="page-header__title">Operations</h1>
                        <p className="page-header__description">
                            Live snapshot of the intel platform — ingest velocity, catalog size,
                            feed health, and recent additions. Metrics refresh on navigation; feed
                            cadence is tracked against each source's configured schedule.
                        </p>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item">
                        {noc.isLive ? (
                            <>
                                <span className="live-dot live-dot--success" aria-hidden />
                                Live
                            </>
                        ) : (
                            <span className="chip">Polling 10s</span>
                        )}
                    </span>
                    <span className="page-header__meta__item">
                        <span className="live-dot live-dot--success" aria-hidden />
                        {healthyFeeds.length.toLocaleString()} / {feedsAll.length.toLocaleString()} feeds healthy
                    </span>
                    {failingFeeds.length > 0 && (
                        <span
                            className="page-header__meta__item tabular-nums"
                            style={{ color: "var(--color-danger)" }}
                        >
                            {failingFeeds.length} failing
                        </span>
                    )}
                </div>
            </header>

            {/* Metric grid */}
            <section className="metric-grid" aria-label="Platform metrics">
                <div className="metric-card metric-card--accent">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><Database size={12} aria-hidden /></span>
                        Tracked IoCs
                    </span>
                    <span className="metric-card__value">
                        {stats.data ? stats.data.total.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">Deduplicated across all feeds</span>
                </div>

                <div className="metric-card">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><Activity size={12} aria-hidden /></span>
                        Sightings · 24h
                    </span>
                    <span className="metric-card__value">
                        {stats.data ? stats.data.sightings24h.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">
                        {stats.data
                            ? `${stats.data.sightings1h.toLocaleString()} in last hour`
                            : "Rolling 24h window"}
                    </span>
                    <span style={{ position: "absolute", top: "var(--space-2)", right: "var(--space-2)" }}>
                        <GrafanaLink panelId="ioc-sightings" metric="IoC sightings" panelName="IoC ingestion velocity" eta="Once Grafana datasource is wired to ClickHouse" />
                    </span>
                </div>

                <div className="metric-card">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><Bug size={12} aria-hidden /></span>
                        CVEs tracked
                    </span>
                    <span className="metric-card__value">
                        {cves.data ? cves.data.total.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">CISA KEV catalog</span>
                </div>

                <div className="metric-card metric-card--danger">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon" style={{ color: "var(--color-danger)" }}>
                            <AlertTriangle size={12} aria-hidden />
                        </span>
                        Ransomware KEV
                    </span>
                    <span className="metric-card__value">
                        {cvesRansomware.data ? cvesRansomware.data.total.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">Known ransomware exploitation</span>
                </div>

                <div className="metric-card">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><Rss size={12} aria-hidden /></span>
                        Active feeds
                    </span>
                    <span className="metric-card__value">
                        {feeds.data ? feedsAll.length.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">
                        {feeds.data
                            ? `${healthyFeeds.length} healthy · ${failingFeeds.length} failing`
                            : "Ingestion sources"}
                    </span>
                </div>

                <div
                    className={
                        failingFeeds.length > 0 ? "metric-card metric-card--danger" : "metric-card metric-card--success"
                    }
                >
                    <span className="metric-card__label">
                        <span
                            className="metric-card__label__icon"
                            style={{
                                color:
                                    failingFeeds.length > 0
                                        ? "var(--color-danger)"
                                        : "var(--color-success)",
                            }}
                        >
                            <Radio size={12} aria-hidden />
                        </span>
                        Failing feeds
                    </span>
                    <span className="metric-card__value">
                        {feeds.data ? failingFeeds.length.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">
                        {failingFeeds.length === 0 ? "All sources nominal" : "Investigate on the Feeds page"}
                    </span>
                    <span style={{ position: "absolute", top: "var(--space-2)", right: "var(--space-2)" }}>
                        <GrafanaLink panelId="feed-failures" metric="Failing feeds" panelName="Feed health timeline" eta="Once Grafana is wired to Postgres feed_runs" />
                    </span>
                </div>
            </section>

            {/* IoC type breakdown section */}
            <section className="section-card" aria-label="IoC type breakdown">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">IoC types</h2>
                        <p className="section-card__subtitle">
                            Live count of rows in the iocs catalog by type.
                        </p>
                    </div>
                </div>
                {iocStats.isLoading ? (
                    <div style={{ padding: "var(--space-4)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "var(--space-3)" }}>
                            {[1, 2, 3].map((i) => (
                                <div key={i} style={{ background: "var(--color-surface)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", minHeight: "80px" }} />
                            ))}
                        </div>
                    </div>
                ) : iocStats.isError ? (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load IoC type stats</div>
                    </div>
                ) : iocTypeCards.length === 0 ? (
                    <div className="empty-state">
                        <Database size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No IoCs yet</div>
                    </div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "var(--space-3)", padding: "var(--space-4)" }}>
                        {iocTypeCards.map((bucket) => (
                            <Link
                                key={bucket.key}
                                to="/iocs"
                                search={{ type: bucket.key }}
                                style={{ textDecoration: "none", color: "inherit" }}
                            >
                                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", textAlign: "center", cursor: "pointer", transition: "background-color 0.2s" }}>
                                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "var(--space-2)" }}>
                                        {bucket.key}
                                    </div>
                                    <div className="tabular" style={{ fontSize: "var(--font-size-24)", fontWeight: 600 }}>
                                        {bucket.count.toLocaleString()}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Severity distribution — horizontal bars from iocs.severity */}
            <section className="section-card" aria-label="Severity distribution">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Severity distribution</h2>
                        <p className="section-card__subtitle">
                            Count of IoCs in the catalog by severity score (1 = lowest, 10 = highest).
                        </p>
                    </div>
                </div>
                {(() => {
                    const buckets = (iocStats.data?.bySeverity ?? []).filter((b) => b.count > 0);
                    if (iocStats.isLoading) {
                        return (
                            <div style={{ padding: "var(--space-4)" }}>
                                <div style={{ height: "120px", background: "var(--color-surface)", borderRadius: "var(--radius-md)" }} />
                            </div>
                        );
                    }
                    if (buckets.length === 0) {
                        return (
                            <div className="empty-state">
                                <Database size={20} className="empty-state__icon" aria-hidden />
                                <div className="empty-state__title">No scored IoCs yet</div>
                            </div>
                        );
                    }
                    const max = Math.max(...buckets.map((b) => b.count));
                    return (
                        <div style={{ padding: "var(--space-4)", display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "var(--space-2) var(--space-3)", alignItems: "center" }}>
                            {buckets.map((b) => {
                                const pct = max > 0 ? (b.count / max) * 100 : 0;
                                const sev = parseInt(b.key, 10);
                                const tone =
                                    sev >= 8 ? "var(--color-danger)"
                                    : sev >= 6 ? "var(--color-warning)"
                                    : sev >= 4 ? "var(--color-accent)"
                                    : "var(--color-text-muted)";
                                return (
                                    <Fragment key={b.key}>
                                        <div className="text-13 tabular-nums" style={{ minWidth: "2.5em", textAlign: "right", color: tone }}>
                                            sev {b.key}
                                        </div>
                                        <div style={{ background: "var(--color-surface)", borderRadius: "var(--radius-sm)", height: "12px", overflow: "hidden" }}>
                                            <div style={{ background: tone, width: `${pct}%`, height: "100%", transition: "width 0.3s" }} />
                                        </div>
                                        <div className="text-13 tabular-nums" style={{ minWidth: "5em", textAlign: "right" }}>
                                            {b.count.toLocaleString()}
                                        </div>
                                    </Fragment>
                                );
                            })}
                        </div>
                    );
                })()}
            </section>

            {/* Heuristic engine summary tiles — Phase 2d */}
            <section className="section-card" aria-label="Heuristic engine">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">
                            <Sparkles size={14} aria-hidden style={{ marginRight: 6, verticalAlign: -2 }} />
                            Heuristic Engine
                        </h2>
                        <p className="section-card__subtitle">
                            Async deep-analysis queue + 24h calibration. <Link to="/heuristic">Open test page →</Link>
                        </p>
                    </div>
                </div>
                {heuristicStats.isLoading ? (
                    <div style={{ padding: "var(--space-4)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--space-3)" }}>
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} style={{ background: "var(--color-surface)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", minHeight: "80px" }} />
                            ))}
                        </div>
                    </div>
                ) : heuristicStats.isError ? (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Heuristic stats unavailable</div>
                    </div>
                ) : heuristicStats.data ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--space-3)", padding: "var(--space-4)" }}>
                        {[
                            { label: "Queue depth", value: heuristicStats.data.queueDepth.toLocaleString(), sub: `${heuristicStats.data.queueRunning.toLocaleString()} running` },
                            { label: "Verdicts (24h)", value: heuristicStats.data.verdicts24h.toLocaleString(), sub: `${heuristicStats.data.hotVerdicts.toLocaleString()} hot in cache` },
                            { label: "Agreement", value: heuristicStats.data.calibrations24h === 0 ? "—" : `${heuristicStats.data.agreementRate24h.toFixed(1)}%`, sub: `${heuristicStats.data.calibrations24h.toLocaleString()} calibrations` },
                            { label: "Promoted (24h)", value: heuristicStats.data.promotedIocs24h.toLocaleString(), sub: heuristicStats.data.latencyP50Ms > 0 ? `p50 ${heuristicStats.data.latencyP50Ms} ms` : "no recent runs" },
                        ].map((bucket) => (
                            <Link
                                key={bucket.label}
                                to="/heuristic"
                                style={{ textDecoration: "none", color: "inherit" }}
                            >
                                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", cursor: "pointer", transition: "background-color 0.2s" }}>
                                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "var(--space-2)" }}>
                                        {bucket.label}
                                    </div>
                                    <div className="tabular" style={{ fontSize: "var(--font-size-22)", fontWeight: 600 }}>
                                        {bucket.value}
                                    </div>
                                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-12)", marginTop: 4 }}>
                                        {bucket.sub}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : null}
            </section>

            {/* Per-feed contribution section */}
            <section className="section-card" aria-label="Per-feed contribution">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Feed contributions</h2>
                        <p className="section-card__subtitle">
                            Cumulative IoC ingest count per enabled feed source.
                        </p>
                    </div>
                    {restCount > 0 && (
                        <div className="text-text-muted" style={{ fontSize: "var(--font-size-12)" }}>
                            +{restCount} more
                        </div>
                    )}
                </div>
                {feeds.isLoading ? (
                    <div style={{ padding: "var(--space-4)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "var(--space-3)" }}>
                            {[1, 2, 3].map((i) => (
                                <div key={i} style={{ background: "var(--color-surface)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", minHeight: "80px" }} />
                            ))}
                        </div>
                    </div>
                ) : feeds.isError ? (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load feed sources</div>
                    </div>
                ) : enabledFeeds.length === 0 ? (
                    <div className="empty-state">
                        <Rss size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No enabled feeds</div>
                    </div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "var(--space-3)", padding: "var(--space-4)" }}>
                        {topFeeds.map((feed) => (
                            <Link
                                key={feed.slug}
                                to="/feeds"
                                style={{ textDecoration: "none", color: "inherit" }}
                            >
                                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", cursor: "pointer", transition: "background-color 0.2s" }}>
                                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "var(--space-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={feed.name}>
                                        {feed.name}
                                    </div>
                                    <div className="tabular" style={{ fontSize: "var(--font-size-24)", fontWeight: 600 }}>
                                        {(feed.ingestedCount ?? 0).toLocaleString()}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Dependency health — admin-only. Tiles update on a 5s poll
                (the BFF caches the probe set for 5s so the cadence is
                cheap). When a dep has no env config the tile renders a
                muted "Not configured" chip rather than a false alarm. */}
            {isAdmin && (
                <section className="section-card" aria-label="Dependency health">
                    <div className="section-card__header">
                        <div className="section-card__title-group">
                            <h2 className="section-card__title">Dependencies</h2>
                            <p className="section-card__subtitle">
                                Reachability + p50 latency for each platform infrastructure
                                component. Probes run every 5 seconds with a 2-second
                                per-probe deadline.
                            </p>
                        </div>
                    </div>
                    {depHealth.isLoading ? (
                        <TableSkeleton rows={2} columns={4} />
                    ) : depHealth.isError ? (
                        <div className="empty-state">
                            <ShieldX size={20} className="empty-state__icon" aria-hidden />
                            <div className="empty-state__title">Could not probe dependencies</div>
                            <div className="empty-state__body">
                                The dep-health endpoint returned an error. Try refreshing or
                                check the admin-bff logs.
                            </div>
                        </div>
                    ) : (depHealth.data?.deps.length ?? 0) === 0 ? (
                        <div className="empty-state">
                            <Server size={20} className="empty-state__icon" aria-hidden />
                            <div className="empty-state__title">No dependencies registered</div>
                        </div>
                    ) : (
                        <div className="metric-grid" aria-label="Dependency tiles">
                            {depHealth.data?.deps.map((d) => (
                                <DepHealthTile key={d.name} dep={d} />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Feed health */}
            <section className="section-card" aria-label="Feed health">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Feed health</h2>
                        <p className="section-card__subtitle">
                            Sources ranked by how far past schedule their last successful run is.
                        </p>
                    </div>
                    <div className="section-card__actions cluster-xs">
                        <GrafanaLink panelId="feed-lag" metric="Per-feed schedule lag" panelName="Feed lag heatmap" eta="When Grafana is provisioned" />
                        <Link to="/feeds" className="btn btn--ghost btn--sm press-crush">
                            View all
                        </Link>
                    </div>
                </div>
                {feeds.isLoading ? (
                    <TableSkeleton rows={5} columns={4} />
                ) : feeds.isError ? (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load feeds</div>
                        <div className="empty-state__body">Try refreshing in a moment.</div>
                    </div>
                ) : feedsAll.length === 0 ? (
                    <div className="empty-state">
                        <Rss size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No feeds configured yet</div>
                        <div className="empty-state__body">Feeds ship in Phase 1C.</div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table data-table--dense">
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Last run</th>
                                    <th
                                        style={{
                                            textAlign: "right",
                                            whiteSpace: "nowrap",
                                            width: 1,
                                        }}
                                        title="New IoCs added on the most recent run, after dedup against the existing catalog. Not the row count fetched."
                                    >
                                        New IoCs
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {laggingFeeds.map((f) => {
                                    const statusClass =
                                        f.lastStatus === "ok"
                                            ? "status-pill--live"
                                            : f.lastStatus === "error"
                                              ? "status-pill--failed"
                                              : "status-pill--queued";
                                    const isLive = f.lastStatus === "ok";
                                    return (
                                        <tr key={f.slug}>
                                            <td style={{ maxWidth: 0, width: "100%" }}>
                                                <div className="stack-xs" style={{ minWidth: 0 }}>
                                                    <span
                                                        className="text-13"
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                        title={f.name}
                                                    >
                                                        {f.name}
                                                    </span>
                                                    <code
                                                        className="type-mono-sm text-text-muted"
                                                        style={{ fontSize: "var(--font-size-11)" }}
                                                    >
                                                        {f.slug}
                                                    </code>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <span className={`status-pill ${statusClass}`}>
                                                    {f.lastStatus ?? "pending"}
                                                </span>
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <span className="cluster-xs">
                                                    {isLive && (
                                                        <span
                                                            className="live-dot live-dot--success"
                                                            aria-hidden
                                                        />
                                                    )}
                                                    <span className="text-12 text-text-muted tabular-nums">
                                                        {formatRelative(f.lastRunAt)}
                                                    </span>
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "right",
                                                    whiteSpace: "nowrap",
                                                }}
                                                className="text-13 tabular-nums"
                                            >
                                                {f.lastIngestedCount.toLocaleString()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Recent activity */}
            <section className="section-card" aria-label="Recent activity">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Latest indicators</h2>
                        <p className="section-card__subtitle">
                            Ten most recently seen IoCs across all feeds.
                        </p>
                    </div>
                    <div className="section-card__actions">
                        <Link to="/iocs" className="btn btn--ghost btn--sm press-crush">
                            Open IoCs
                        </Link>
                    </div>
                </div>
                {recent.isLoading ? (
                    <TableSkeleton rows={6} columns={3} />
                ) : recent.isError ? (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load IoCs</div>
                    </div>
                ) : (recent.data?.iocs.length ?? 0) === 0 ? (
                    <div className="empty-state">
                        <Database size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No IoCs yet</div>
                        <div className="empty-state__body">Waiting on the first ingest tick.</div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table data-table--dense">
                            <thead>
                                <tr>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Type</th>
                                    <th>Indicator</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Severity</th>
                                    <th
                                        style={{
                                            whiteSpace: "nowrap",
                                            width: 1,
                                            textAlign: "right",
                                        }}
                                    >
                                        Last seen
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.data?.iocs.map((i) => (
                                    <tr key={i.id}>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className="chip">{i.type}</span>
                                        </td>
                                        <td style={{ maxWidth: 0, width: "100%" }}>
                                            <span
                                                className="type-mono-sm"
                                                title={i.value}
                                                style={{
                                                    display: "block",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {i.value}
                                            </span>
                                        </td>
                                        <td
                                            style={{ whiteSpace: "nowrap" }}
                                            className="tabular-nums text-13 text-text-muted"
                                        >
                                            {i.severity}
                                        </td>
                                        <td
                                            className="text-12 text-text-muted tabular-nums"
                                            style={{
                                                textAlign: "right",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {formatRelative(i.lastSeen)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
